import { MinusCircleOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { getEditableUsers, updateUser } from "admin/rest_api";
import { AutoComplete, Flex, Input, Modal, Spin, Tooltip } from "antd";
import type { DefaultOptionType } from "antd/lib/select";
import { useQueryWithErrorHandling } from "libs/react_hooks";
import { useState } from "react";
import type { APITeam, APITeamMembership, APIUser } from "types/api_types";
import { filterTeamMembersOf, renderUsersForTeam } from "./team_list_view";

type Props = {
  onCancel: (...args: Array<any>) => any;
  isOpen: boolean;
  team: APITeam | null;
};

function EditTeamModalForm({ onCancel, isOpen, team }: Props) {
  const queryClient = useQueryClient();
  const { data: users = [], isPending } = useQueryWithErrorHandling({
    queryKey: ["editableUsers"],
    queryFn: getEditableUsers,
    refetchOnWindowFocus: false,
  });

  const [autoCompleteValue, setAutoCompleteValue] = useState("");
  const [dropDownVisible, setDropDownVisible] = useState(false);
  const onChange = (newValue: string) => setAutoCompleteValue(newValue);
  const [isWaitingForRequest, setIsWaitingForRequest] = useState(false);

  if (team === null) return null;
  const updateTeamMembership = async (user: APIUser, newTeams: APITeamMembership[]) => {
    setIsWaitingForRequest(true);
    setDropDownVisible(false);
    const newUser = Object.assign({}, user, {
      teams: newTeams,
    });
    await updateUser(newUser);
    queryClient.invalidateQueries({ queryKey: ["editableUsers"] });
    setIsWaitingForRequest(false);
  };

  const addTo = async (user: APIUser, team: APITeam | null) => {
    if (team === null) return;
    const newTeam: APITeamMembership = { id: team.id, name: team.name, isTeamManager: false };
    updateTeamMembership(user, [...user.teams, newTeam]);
  };

  const removeFrom = async (user: APIUser, team: APITeam | null) => {
    const newTeams = user.teams.filter((userteam) => team?.id !== userteam.id);
    updateTeamMembership(user, newTeams);
  };

  const renderRemoveSpan = (user: APIUser) => {
    if (user.isAdmin) {
      return (
        <Tooltip title="Admins cannot be removed because they can access all teams.">
          <span>Admin</span>
        </Tooltip>
      );
    }
    return (
      <span onClick={() => removeFrom(user, team)}>
        <MinusCircleOutlined /> Remove from {team?.name}
      </span>
    );
  };

  const renderTeamMember = (user: APIUser): DefaultOptionType => ({
    value: `${user.firstName} ${user.lastName} ${user.email}`,
    label: (
      <Flex justify="space-between">
        {user.firstName} {user.lastName}
        {renderRemoveSpan(user)}
      </Flex>
    ),
  });

  const renderUserNotInTeam = (user: APIUser): DefaultOptionType => ({
    value: `${user.firstName} ${user.lastName} ${user.email}`,
    label: (
      <Flex justify="space-between">
        <span>
          {user.firstName} {user.lastName}
        </span>
        <span onClick={() => addTo(user, team)}>
          <PlusCircleOutlined /> Add to {team?.name}
        </span>
      </Flex>
    ),
  });

  const activeUsers = users.filter((user) => user.isActive);
  const options: DefaultOptionType[] = [
    {
      label: "In team",
      options: activeUsers
        .filter((user) => filterTeamMembersOf(team, user))
        .map((user) => renderTeamMember(user)),
    },
    {
      label: "Not in team",
      options: activeUsers
        .filter((user) => !filterTeamMembersOf(team, user))
        .map((user) => renderUserNotInTeam(user)),
    },
  ];

  const renderRemoveInStaticTeamList = (user: APIUser, team: APITeam) => {
    if (user.isAdmin) return null;
    return (
      <Tooltip title="Remove from team">
        <MinusCircleOutlined onClick={() => removeFrom(user, team)} />
      </Tooltip>
    );
  };

  const renderModalBody = () => {
    return (
      <Spin spinning={isWaitingForRequest}>
        <AutoComplete
          style={{ width: "100%", marginBottom: "16px" }}
          options={options}
          onSelect={() => {
            setAutoCompleteValue("");
          }}
          value={autoCompleteValue}
          onChange={onChange}
          open={dropDownVisible}
          onFocus={() => setDropDownVisible(true)}
          onBlur={() => setDropDownVisible(false)}
          showSearch={{
            onSearch: () => setDropDownVisible(true),
            filterOption: (inputValue, option) => {
              return (
                inputValue === "" ||
                (typeof option?.value === "string" &&
                  option?.value?.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1)
              );
            },
          }}
          onOpenChange={() => setAutoCompleteValue("")}
        >
          <Input.Search size="large" placeholder="Search users" />
        </AutoComplete>
        {renderUsersForTeam(team, users, renderRemoveInStaticTeamList)}
      </Spin>
    );
  };
  return (
    <Modal open={isOpen} onCancel={onCancel} title="Add / Remove Users" footer={null} width={800}>
      <Spin spinning={isPending}>{isPending ? null : renderModalBody()}</Spin>
    </Modal>
  );
}

const EditTeamModalView = EditTeamModalForm;
export default EditTeamModalView;
